/**
 * PATH: packages/tenant-shared/src/settings/quotechSettings.ts
 * FILE_ID: TENANTSHARED-QUOTECHSETTINGS-001
 * PURPOSE: Tenant-agnostic Quotech settings store (notification +
 *          email + file + navigation preferences, organisation
 *          settings record, 2FA setup, account/session info).
 * WHY:     Byte-identical between newmark and sam pre-MR #7 — ~29 KB
 *          of code that was being maintained twice. The stored
 *          shapes are dictated by master backend endpoints, so
 *          there's no reason to clone.
 * DEPENDENCIES:
 *   - ../services/quotechApi — was `@/shared/services/quotechApi`
 *     in tenants; now resolved relatively within the package.
 * RELATED:
 *   - apps/{newmark,sam}/src/features/settings/quotechSettings.ts —
 *     thin re-export shims (post-MR #7).
 */

import { quotechJson } from '../services/quotechApi'

export interface NotificationPreferences {
  email: {
    enabled: boolean
    jobUpdates: boolean
    mentions: boolean
    dailyDigest: boolean
    weeklyReport: boolean
  }
  push: {
    enabled: boolean
    urgentOnly: boolean
    sound: boolean
  }
  inApp: {
    enabled: boolean
    showBadge: boolean
  }
}

export interface EmailPreferences {
  sender_name: string
  reply_to: string
  signature: string
  digest_frequency: 'none' | 'daily' | 'weekly'
  auto_cc: boolean
  delivery_failure_alerts: boolean
  // AI Kanban auto-apply switch (FEAT-2026-06-14-AI-AUTO-APPLY).
  // false (default) → AI files emails as AI_SUGGESTED_PENDING: they
  // stay private and show the ✓/✗ approve banner until this user
  // accepts. true → AI files straight to the board (AI_AUTO), visible
  // to board members immediately. Always present (backend defaults it).
  ai_auto_apply_allocations: boolean
  // "Powered by Quotech" GTM footer — OPT-IN (FEAT-2026-07-07). false
  // (default) sends without the marketing footer; true adds it below the
  // signature on customer-facing sends. Always present (backend defaults
  // it False — the footer never ships unless explicitly enabled).
  quotech_branding: boolean
}

export interface FilePreferences {
  auto_sync: boolean
  default_visibility: 'private' | 'team' | 'organisation'
  retention_days: number
  notify_on_upload: boolean
  default_folder: string
}

export interface NavigationPreferences {
  hub_order: string[]
  pinned_pages: string[]
  ai_auto_order: boolean
  usage_stats: Record<string, number>
  quick_access_shortcuts: string[]
  theme: 'light' | 'dark' | 'system'
  sidebar_collapsed: boolean
}

/**
 * Generic per-user cross-hub UI preferences (backed by
 * /api/settings/ui/). Extend as more cross-hub UI prefs land.
 *
 * The backend (organisation/.../settings_views) does a PARTIAL top-level
 * merge, so a PUT carrying only one key never clobbers the others — each
 * caller (useShortcutsHubTab, useShortcutOverrides) saves just its slice.
 */
/**
 * Per-user schedule weather/clock location override. `mode === 'org'` (the
 * default) follows the organisation's weather city; `'current'` (my location)
 * and `'custom'` (a picked city) carry a lat/lng (+ optional label) that
 * re-point BOTH the weather data and the planner clock for this user. The
 * backend `_clean_weather_location` validates this exact shape and fail-softs a
 * coord-less current/custom back to `{mode:'org'}`.
 */
export type WeatherLocationMode = 'org' | 'current' | 'custom'

export interface WeatherLocationPref {
  mode: WeatherLocationMode
  lat?: number
  lng?: number
  label?: string
}

export interface UiPreferences {
  /** Last-selected hub tab on the Settings → Shortcuts reference. */
  shortcuts_hub_tab: string
  /**
   * Per-user keyboard-shortcut customizations, keyed by ShortcutAction id.
   * Structurally identical to @quotech/ui's ShortcutOverrideMap but inlined
   * here to avoid a tenant-shared → @quotech/ui dependency edge. Absent /
   * empty = all defaults. Each entry: `binding` (remap; null = unbound) and/or
   * `enabled` (false = user-disabled).
   */
  shortcut_overrides?: Record<string, { binding?: string | null; enabled?: boolean }>
  /** Per-user schedule weather/clock location override (see WeatherLocationPref). */
  weather_location?: WeatherLocationPref
}

export interface OrganisationSettingsRecord {
  name: string
  company_name: string
  abn: string
  acn: string
  email: string
  phone: string
  website: string
  address: string
  city: string
  state: string
  postcode: string
  country: string
  timezone: string
  /**
   * Organisation default weather city for the schedule strips + planner clock.
   * Free-text city/address; the platform geocodes it and provisions an
   * `org_city` WeatherLocation. Blank = fall back to the auto-geocoded HQ
   * address ladder (non-regressive). A per-user override (UiPreferences
   * `weather_location`) beats this for that user.
   */
  weather_location: string
  /**
   * Stack 11 PR 7 — Job Board Access RBAC.
   *
   * List of SystemRole.code values (e.g. ``'org_owner'``,
   * ``'general_manager'``, ``'project_manager'``,
   * ``'construction_manager'``) whose users can grant or revoke
   * BoardMembership / LaneMembership rows on email_kanban boards.
   *
   * ``'org_owner'`` is always allowed regardless of this list (the
   * backend enforces this; the UI hides the option to remove it).
   * Default backfilled to all four codes by org migration 0119.
   *
   * Owner-only edit gate — ``rbac.is_org_owner`` server-side, NOT
   * delegable to Org Admins.
   */
  job_board_admin_role_codes: string[]

  // ===== Newmark Delivery Arc tenant flags =====
  //
  // All five surfaces feed the Settings hub "Email Kanban
  // Automation" tab. Defaults are safe for non-Newmark tenants:
  // the toggle is OFF, the lane-names list is empty, and the
  // template id is blank — so flipping nothing here is identical
  // to the pre-arc behaviour.
  //
  // See backend migrations 0151 / 0152 / 0153 for the schema and
  // docs/customers/NEWMARK_TRELLO_USER_MAP.md for the operator
  // contract.

  /**
   * Stack 2 (workflow-map F7) — when true, route_email_to_card
   * moves the source email out of the provider's inbox once a
   * Kanban card lands. Requires gmail.modify / Mail.ReadWrite
   * scopes on the synced account.
   */
  email_kanban_writeback_on_processed: boolean
  /** Stack 2 — Gmail label name written by the writeback service. */
  email_kanban_writeback_gmail_label: string
  /** Stack 2 — Outlook folder name written by the writeback service. */
  email_kanban_writeback_outlook_folder: string
  /**
   * Stack 5 (workflow-map F10) — ordered JOB-board lane names that
   * the Sunday-night beat task shifts left every Sunday 23:00
   * org-local. Empty list = no shift.
   */
  email_kanban_weekly_schedule_lane_names: string[]
  /**
   * Stack 1 (workflow-map F2) — when true, classified emails always land
   * on the personal INBOX board first and mirror to the job/finance board
   * when a job is resolved. When false, bill/variation mail may skip the
   * inbox and go straight to the job board.
   */
  email_kanban_dual_board_routing: boolean
  /**
   * Stack 7 (workflow-map F9) — Graph item-id of the OneDrive
   * "template job" folder. When set, new Jobs auto-clone this
   * folder. Blank = no clone.
   */
  folder_template_drive_id: string
}

export interface QuotechSessionInfo {
  id: string
  device: string
  browser: string
  ip: string
  location: string
  lastActive: string
  isCurrent: boolean
}

export interface TwoFactorStatus {
  enabled: boolean
  backup_codes_remaining: number
  last_used: string | null
  setup_required: boolean
}

export interface TwoFactorSetupPayload {
  secret: string
  provisioning_uri: string
  qr_code: string
  message: string
}

export interface TwoFactorConfirmPayload {
  success: boolean
  backup_codes: string[]
  message: string
}

/**
 * Coarse per-member access preset (mirrors OrganisationMember.access_preset):
 *  - 'full'            — default; role rules apply as normal.
 *  - 'project_scoped'  — site worker: only jobs/boards they're granted/created
 *                        on, own + board-allocated emails, no org-wide hubs.
 *  - 'diary_only'      — clamped to diary/emails/calendar.
 */
export type AccessPreset = 'full' | 'project_scoped' | 'diary_only'

export interface TeamMember {
  id: number
  uid?: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: string
  status: 'active' | 'inactive'
  is_temp_password: boolean
  /**
   * True for Quotech-internal accounts (platform admins, dev team) that
   * are members of this organisation for support / debugging purposes.
   * Hidden from the team list by default; shown when the customer admin
   * toggles "Show platform support" (which sets `?include_support=true`
   * on the API call). When shown, the UI marks them with a small badge
   * so the customer can tell internal accounts apart from their own
   * teammates.
   */
  is_platform_support?: boolean
  date_joined: string | null
  last_login: string | null
  /**
   * Coarse invite-state enum used by the SAM Settings → Team Members
   * table to render a single status pill + the right Resend label
   * without further client-side derivation. Set by the backend in
   * `TeamMembersListCreateAPI.get`.
   *
   *   - `pending`  : Has temp password OR has never logged in.
   *                  Resend should re-issue the invite email and
   *                  rotate the temp password.
   *   - `active`   : Has logged in at least once. Resend should
   *                  send a "share this sign-in link" email
   *                  WITHOUT touching the password.
   *   - `inactive` : Account explicitly disabled. Resend disabled.
   */
  invite_status?: 'pending' | 'active' | 'inactive'
  /**
   * Pre-built deep-link to the master Quotech sign-in page that
   * forces the login form to render and pre-fills the invitee's
   * email. Includes `?invite_for=<email>&org=<id>&allowLogin=1`.
   * Used by the table's "Copy invite link" button.
   */
  invite_url?: string
  /**
   * True for a "pending onboarding" member — added by name (e.g. a site
   * crew member typed into the @mention composer) before they have a
   * login. Their synthetic placeholder email is blanked server-side, so
   * the UI should badge them "awaiting onboarding" rather than show a
   * fake address. Set by the backend in the members list + quick-add.
   */
  is_pending_onboarding?: boolean
  profile_image?: string | null
  contact?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  /**
   * Industry/job-title position (e.g. 'Project Manager', 'Estimator',
   * 'Site Supervisor', 'Boilermaker'). Free-text; the SAM Settings UI
   * offers a curated dropdown of Steel/Construction roles via
   * `teamMemberCatalog.POSITION_SUGGESTIONS` but accepts any value.
   */
  position?: string | null
  /**
   * Employment relationship — orthogonal to RBAC `role`.
   *
   * DEPRECATED in favour of `worker_types` (multi-value list). Kept
   * for one release so old code paths keep working — equals
   * `worker_types[0]` (or '' when the list is empty). New consumers
   * should always read `worker_types` so they handle the
   * Director-AND-Employee case correctly.
   */
  worker_type?: string | null
  /**
   * Employment relationship(s) — list of WorkerTypeValue strings.
   * A single person can hold MULTIPLE types at once (e.g. a
   * working director is both 'director' and 'employee').
   */
  worker_types?: string[]
  /** Primary work location. Free-text. */
  location?: string | null
  /**
   * Per-member hub access overrides ({hub_name: bool}). Used by
   * the SAM Team Members "Advanced Permissions" expander to grant
   * or revoke specific hubs at invite time. `null` / missing key
   * means the role's default applies.
   */
  hub_access_overrides?: Record<string, boolean>
  /**
   * Coarse access preset. Editable here and in People → Access — both
   * write the same OrganisationMember column, so the two screens stay in
   * sync. `undefined` from an older backend is treated as 'full'.
   */
  access_preset?: AccessPreset
}

export interface TeamMembersResponse {
  members: TeamMember[]
  total: number
  /**
   * Number of platform-support accounts (Quotech-internal admin / dev
   * users) that are members of this organisation but were FILTERED OUT of
   * `members` because the request did not pass `?include_support=true`.
   * Used by the SAM Team Members tab to render a small "X support
   * accounts hidden — Show" affordance.
   */
  total_hidden_support_count: number
  /** Mirrors the resolved value of the `?include_support` query param. */
  include_support: boolean
  organisation: {
    id: number
    name: string
  }
}

export interface TeamMemberMutationResponse {
  success: boolean
  message: string
  /**
   * True when the invitation email was successfully handed off to the
   * SMTP server. False when the membership was created but the email
   * could not be sent — surface this distinction in the UI so admins
   * know whether to re-share credentials manually.
   */
  email_sent?: boolean
  email_error?: string | null
  member?: {
    id: number
    email: string
    role: string
    is_active?: boolean
    is_new_user?: boolean
    position?: string
    worker_type?: string
    worker_types?: string[]
    location?: string
    hub_access_overrides?: Record<string, boolean>
    /** True when the member was onboarded with a generated proxy alias. */
    is_proxy_email?: boolean
    restore_email?: string | null
  }
}

export interface InviteMemberPayload {
  email: string
  first_name: string
  last_name: string
  role: string
  /** Optional org-chart fields (all default to empty string on backend). */
  position?: string
  /**
   * Multi-value worker-types list. The backend accepts both
   * `worker_types` (preferred) and the legacy `worker_type` string —
   * we always send the plural form so a working-director invite
   * goes through as `['director', 'employee']`.
   */
  worker_types?: string[]
  location?: string
  /** Per-hub access overrides set at invite time (Advanced Permissions). */
  hub_access_overrides?: Record<string, boolean>
  /**
   * Email-optional onboarding. When true and `email` is blank, the
   * backend mints a proxy login alias (first.last@<tenant-alias-domain>)
   * so a worker without an email still gets a login + timesheet identity.
   */
  generate_proxy_email?: boolean
  /** Optional real/personal email captured to seed the later claim flow. */
  restore_email?: string
}

export interface UpdateMemberPayload {
  /** RBAC role. Only an org admin can change this; cannot self-edit. */
  role?: string
  /** Account active flag. Admin-only. */
  is_active?: boolean
  /** Org-chart fields — admins can edit anyone's; users can edit their own. */
  position?: string
  /** Multi-value worker types. Same semantics as in InviteMemberPayload. */
  worker_types?: string[]
  location?: string
  /** Per-member hub access overrides ({hub_name: bool}). Admin-only. */
  hub_access_overrides?: Record<string, boolean>
  /**
   * Coarse access preset (full / project_scoped / diary_only). Admin-only;
   * the backend applies the matching permission-overrides when it changes.
   */
  access_preset?: AccessPreset
  /**
   * Profile fields — first/last name + contact phone. The backend
   * detail endpoint (organisation/views/team_members_views/
   * detail_views.py) currently only handles role / is_active /
   * position / worker_types / location / hub_access_overrides on
   * PUT — these extra fields are accepted speculatively here so we
   * don't have to bump the type when the backend gains them. The
   * backend silently ignores unknown keys today.
   */
  first_name?: string
  last_name?: string
  contact?: string
}

export interface OrganisationEntity {
  id: number
  organisation?: number
  organisation_name?: string
  name: string
  code: string
  xero_tenant_id: string | null
  xero_tenant_name: string | null
  is_default: boolean
  is_active: boolean
  abn: string | null
  legal_name: string | null
  color: string | null
  job_count: number
  created_at?: string
  updated_at?: string
}

export interface OrganisationEntitiesResponse {
  entities: OrganisationEntity[]
  organisation_id: number
  organisation_name: string
}

export interface XeroTenantOption {
  id: string
  name: string
  type: string
  created_date: string | null
  is_linked: boolean
}

export interface XeroTenantListResponse {
  connected: boolean
  needs_reconnection?: boolean
  message?: string
  error?: string
  tenants: XeroTenantOption[]
  total?: number
}

export interface AccessControlSettings {
  page_overrides: Record<string, boolean>
  module_access: Record<string, boolean>
}

export interface QuotechModule {
  id: string
  name: string
  description: string
  icon: string
  url: string
  is_enabled: boolean
  has_access: boolean
}

export interface QuotechModuleListResponse {
  modules: QuotechModule[]
  organisation: {
    id: number
    name: string
  }
}

export interface QuotechRole {
  code: string
  name: string
  description: string
  hierarchy_level?: number
  is_global?: boolean
  is_customizable?: boolean
  permissions?: Record<string, unknown>
  type?: 'system' | 'custom'
}

export interface QuotechRoleListResponse {
  system_roles: QuotechRole[]
  custom_roles: QuotechRole[]
}

export interface QuotechMemberRoleRecord {
  user_id: number
  email: string
  first_name: string
  last_name: string
  role: {
    code: string
    name: string
    type: 'system' | 'custom'
  } | null
  assigned_at: string
  assigned_by: string | null
}

export interface IntegrationStatus {
  connected: boolean
  tenant_name?: string
  organisation_name?: string
  needs_reconnection?: boolean
  message?: string
  error?: string
}

async function getPreferences<T>(path: string, tenantId?: number | null) {
  const { data } = await quotechJson<{ preferences: T }>(path, {
    includeTenant: true,
    tenantId,
  })
  return data.preferences
}

async function putPreferences<T>(path: string, preferences: T, tenantId?: number | null) {
  const { data } = await quotechJson<{ preferences: T }>(path, {
    method: 'PUT',
    includeTenant: true,
    tenantId,
    body: { preferences },
  })
  return data.preferences
}

export function getNotificationPreferences(tenantId?: number | null) {
  return getPreferences<NotificationPreferences>('/api/settings/notifications/', tenantId)
}

export function saveNotificationPreferences(preferences: NotificationPreferences, tenantId?: number | null) {
  return putPreferences('/api/settings/notifications/', preferences, tenantId)
}

export function getEmailPreferences(tenantId?: number | null) {
  return getPreferences<EmailPreferences>('/api/settings/email/', tenantId)
}

export function saveEmailPreferences(preferences: EmailPreferences, tenantId?: number | null) {
  return putPreferences('/api/settings/email/', preferences, tenantId)
}

export function getFilePreferences(tenantId?: number | null) {
  return getPreferences<FilePreferences>('/api/settings/files/', tenantId)
}

export function saveFilePreferences(preferences: FilePreferences, tenantId?: number | null) {
  return putPreferences('/api/settings/files/', preferences, tenantId)
}

export function getUiPreferences(tenantId?: number | null) {
  return getPreferences<UiPreferences>('/api/settings/ui/', tenantId)
}

export function saveUiPreferences(preferences: Partial<UiPreferences>, tenantId?: number | null) {
  return putPreferences('/api/settings/ui/', preferences, tenantId)
}

/** Read just the user's schedule weather/clock location override, defaulting to
 *  org mode when the key is absent (older account / pre-feature). */
export async function getWeatherLocationPref(tenantId?: number | null): Promise<WeatherLocationPref> {
  const prefs = await getUiPreferences(tenantId)
  const wl = prefs?.weather_location
  return wl && wl.mode ? wl : { mode: 'org' }
}

/** Persist the user's weather/clock location override. PARTIAL merge server-side
 *  (carries only this key) so it never clobbers shortcut prefs. */
export function saveWeatherLocationPref(pref: WeatherLocationPref, tenantId?: number | null) {
  return saveUiPreferences({ weather_location: pref }, tenantId)
}

export function getNavigationPreferences(tenantId?: number | null) {
  return getPreferences<NavigationPreferences>('/api/settings/navigation/', tenantId)
}

export function saveNavigationPreferences(preferences: NavigationPreferences, tenantId?: number | null) {
  return putPreferences('/api/settings/navigation/', preferences, tenantId)
}

export async function trackNavigationUsage(hubId: string, pageHref: string, tenantId?: number | null) {
  await quotechJson('/api/settings/navigation/track/', {
    method: 'POST',
    includeTenant: true,
    tenantId,
    body: { hub_id: hubId, page_href: pageHref },
  })
}

export async function getOrganisationSettings(orgId: number) {
  const { data } = await quotechJson<OrganisationSettingsRecord>(`/api/organisations/${orgId}/settings/`)
  return data
}

export async function saveOrganisationSettings(orgId: number, payload: Partial<OrganisationSettingsRecord>) {
  const { data } = await quotechJson<OrganisationSettingsRecord>(`/api/organisations/${orgId}/settings/`, {
    method: 'PATCH',
    body: payload,
  })
  return data
}

export async function getTwoFactorStatus() {
  const { data } = await quotechJson<TwoFactorStatus>('/api/auth/2fa/status/')
  return data
}

export async function startTwoFactorSetup() {
  const { data } = await quotechJson<TwoFactorSetupPayload>('/api/auth/2fa/setup/', {
    method: 'POST',
    body: {},
  })
  return data
}

export async function confirmTwoFactor(code: string) {
  const { data } = await quotechJson<TwoFactorConfirmPayload>('/api/auth/2fa/confirm/', {
    method: 'POST',
    body: { code },
  })
  return data
}

export async function disableTwoFactor(code: string, password?: string) {
  const { data } = await quotechJson<{ success: boolean; message: string }>('/api/auth/2fa/disable/', {
    method: 'POST',
    body: { code, password },
  })
  return data
}

export async function regenerateBackupCodes(code: string) {
  const { data } = await quotechJson<{ success: boolean; backup_codes: string[]; message: string }>('/api/auth/2fa/backup/', {
    method: 'POST',
    body: { code },
  })
  return data
}

export async function getSessions() {
  const { data } = await quotechJson<{ sessions: QuotechSessionInfo[]; total: number }>('/api/auth/sessions/')
  return data.sessions
}

export async function revokeSession(sessionId: string) {
  const { data } = await quotechJson<{ success: boolean; message: string }>('/api/auth/sessions/revoke/', {
    method: 'POST',
    body: { session_id: sessionId },
  })
  return data
}

export async function revokeAllSessions() {
  const { data } = await quotechJson<{ success: boolean; message: string; revoked_count: number }>('/api/auth/sessions/revoke-all/', {
    method: 'POST',
    body: {},
  })
  return data
}

export async function getTeamMembers(orgId: number, opts: { includeSupport?: boolean } = {}) {
  const qs = opts.includeSupport ? '?include_support=true' : ''
  // BUG-2026-06-22-KANBAN-MEMBERS-IDENTITY-RELOAD-LOOP (recurrence of the
  // auth-pipeline-loops family: VIEWPREF-06-10 / GMAIL-STATUS-06-18 /
  // LABELPREFS-06-22). This org-members DIRECTORY read auto-fires on Kanban
  // board render (via buildMembersAdapter → KanbanBoardView members query)
  // for assignee avatars/pickers. On a benign cookie(18)/bearer(19) drift the
  // server stamps `X-User-Id: 18` while the tab is pinned to 19, so the
  // identity guard cleared the pin + scheduled a reload that can NEVER
  // converge → strobe until the call-site breaker trips. The directory is
  // shared org-roster DISPLAY data and the adapter already fails soft (returns
  // []), so it opts out of the reload guard. A genuine drift is still caught by
  // the board's data-bearing card calls (`/api/kanban/...`) + the
  // IdentityMismatchBanner ("Switch Account") — the only correct remediation
  // for a persistent 18-vs-19 drift.
  const { data } = await quotechJson<TeamMembersResponse>(
    `/api/organisations/${orgId}/members/${qs}`,
    { skipIdentityAssert: true },
  )
  return data
}

/**
 * Add a brand-new "pending onboarding" member by display name WITHOUT
 * sending an invite email. Powers the Kanban @mention inline-create
 * ("@mika" → create + assign). Returns the created/reused member row so
 * the caller can drop it straight into the directory cache. Gated
 * server-side by `can_invite_to_org`.
 */
export async function quickAddTeamMember(orgId: number, name: string): Promise<TeamMember> {
  const { data } = await quotechJson<{ member: TeamMember; created: boolean }>(
    `/api/organisations/${orgId}/members/quick-add/`,
    { method: 'POST', body: { name } as Record<string, unknown> },
  )
  return data.member
}

export async function inviteTeamMember(orgId: number, payload: InviteMemberPayload) {
  const { data } = await quotechJson<TeamMemberMutationResponse>(`/api/organisations/${orgId}/members/`, {
    method: 'POST',
    body: { ...payload } as Record<string, unknown>,
  })
  return data
}

// -------------------------------------------------------------------
// Company email-domain registry (Settings → Registered Domains) +
// proxy-worker email claim.
// -------------------------------------------------------------------

export interface OrganisationDomain {
  id: number
  domain: string
  is_verified: boolean
  is_primary: boolean
  verification_method: string
  verified_at: string | null
  created_at: string | null
}

export interface OrganisationDomainsResponse {
  /** Tenant alias domain used to mint proxy worker emails. */
  alias_domain: string
  domains: OrganisationDomain[]
}

export async function getOrganisationDomains(orgId: number) {
  const { data } = await quotechJson<OrganisationDomainsResponse>(
    `/api/organisations/${orgId}/domains/`,
  )
  return data
}

export async function addOrganisationDomain(orgId: number, domain: string) {
  // Returns 409 (handled by quotechJson -> thrown Error) when the
  // domain is already tied to another account.
  const { data } = await quotechJson<OrganisationDomain>(
    `/api/organisations/${orgId}/domains/`,
    { method: 'POST', body: { domain } as Record<string, unknown> },
  )
  return data
}

export async function deleteOrganisationDomain(orgId: number, domainId: number) {
  await quotechJson<unknown>(`/api/organisations/${orgId}/domains/${domainId}/`, {
    method: 'DELETE',
  })
}

export async function detectOrganisationDomains(orgId: number) {
  const { data } = await quotechJson<{ suggestions: string[] }>(
    `/api/organisations/${orgId}/domains/detect/`,
  )
  return data.suggestions
}

export async function updateTeamMember(orgId: number, userId: number, payload: UpdateMemberPayload) {
  const { data } = await quotechJson<TeamMemberMutationResponse>(`/api/organisations/${orgId}/members/${userId}/`, {
    method: 'PUT',
    body: { ...payload } as Record<string, unknown>,
  })
  return data
}

export async function removeTeamMember(orgId: number, userId: number) {
  const { data } = await quotechJson<{ success: boolean; message: string }>(`/api/organisations/${orgId}/members/${userId}/`, {
    method: 'DELETE',
  })
  return data
}

/**
 * Shape returned by ``POST /api/organisations/<org>/members/<user>/resend-invite/``.
 *
 * IMPORTANT: the endpoint always returns HTTP 200 — including on
 * email-send failures — so the rich diagnostic payload survives the
 * shared ``quotechJson`` client (which otherwise collapses non-2xx
 * bodies to ``new Error(detail)`` and loses every field). Inspect
 * ``success`` and ``password_rotated`` to know what actually
 * happened on the server.
 */
export interface ResendInviteResult {
  /**
   * ``true`` when the email left our SMTP relay AND we persisted the
   * rotated temp password. ``false`` when the SMTP send failed —
   * in which case ``password_rotated`` is also ``false`` and the
   * member's credentials are UNCHANGED.
   */
  success: boolean
  /** Operator-readable summary of what just happened. */
  message: string
  /** Pre-built deep-link to the master sign-in page. */
  invite_url?: string
  /**
   * Always ``'pending'`` after a successful resend — the backend
   * unconditionally rotates the password and forces
   * ``is_temp_password=True``, so the user is back in the pending-
   * invite state regardless of what they were before. On failure
   * this echoes the member's PREVIOUS state ('pending' or 'active')
   * so the UI doesn't visually flip the row state on a no-op.
   */
  invite_status?: 'pending' | 'active' | 'inactive'
  /**
   * Distinguishes the two semantics behind the same endpoint:
   *   - ``'resend'``: the user was still in the pending-invite state
   *     (``is_temp_password=True`` already), so this is a reminder.
   *   - ``'reset'``:  the user was activated; the admin force-rotated
   *     their password. The recipient gets a "Password reset" email
   *     and is forced through the change-password flow on next login.
   * On success both modes rotate the password — the difference is the
   * email subject/body and the toast copy.
   */
  mode?: 'resend' | 'reset'

  // ─── Failure-only fields (populated when success === false) ──────
  /**
   * Mirrors ``ExtractedBill``'s style — frontend matches on these
   * values, do not free-text. Kept in sync with
   * ``organisation/services/email_diagnostics.py::CATEGORY_*``.
   *
   * Drives the toast title + colour:
   *   - ``auth_failed`` → red, "Email service auth failed (535)"
   *   - ``connection_failed`` / ``timeout`` → amber, "Try again"
   *   - ``recipient_rejected`` → amber, "Bad recipient"
   *   - ``config_missing`` / ``sender_rejected`` / ``tls_failed`` → red, "Quotech config issue"
   *   - ``unknown`` → red, "Unexpected — see logs"
   */
  error_category?:
    | 'auth_failed'
    | 'connection_failed'
    | 'timeout'
    | 'recipient_rejected'
    | 'sender_rejected'
    | 'config_missing'
    | 'tls_failed'
    | 'unknown'
  /** Numeric SMTP code if available (e.g. ``535`` for auth failure). */
  error_code?: number | null
  /** Python exception class name, e.g. ``"SMTPAuthenticationError"``. */
  error_class?: string
  /**
   * Same string as ``message`` on success; on failure it's the
   * admin-friendly diagnostic ("Email service authentication
   * failed (SMTP code 535). The Quotech mail relay rejected our
   * credentials.").
   */
  error?: string
  /** One-line action hint to show alongside the error. */
  support_hint?: string
  /**
   * Set on failure to ``false`` — the password rotation was rolled
   * back so the recipient's credentials are unchanged. Set to
   * ``true`` on success. Always inspect this before claiming "your
   * member has been signed out".
   */
  password_rotated?: boolean
  /** Echoed To: address(es). Used in the failure toast. */
  to?: string[]
  /** Backend host that failed (e.g. ``smtp.office365.com``). */
  backend_host?: string
  /** ISO timestamp of the send attempt. Useful for log lookups. */
  attempted_at?: string
  /**
   * What the HTTP status WOULD have been if we used standard
   * status codes. We return 200 for client-compatibility (see
   * docstring at top), but operators sometimes want to know the
   * "real" severity tier — 503 vs 400 vs 500.
   */
  http_status_equivalent?: number
}

export interface ResendInvitePayload {
  /** Optional inline name fix-up — patched onto the User row before
   *  the email is sent. Lets admins fix bad data ("last_name = org
   *  name") in the same click as resending the invite. */
  first_name?: string
  last_name?: string
}

export async function resendTeamMemberInvite(
  orgId: number,
  userId: number,
  payload: ResendInvitePayload = {},
) {
  const { data } = await quotechJson<ResendInviteResult>(
    `/api/organisations/${orgId}/members/${userId}/resend-invite/`,
    { method: 'POST', body: payload as Record<string, unknown> },
  )
  return data
}

// ---------------------------------------------------------------------------
// Assignable roles — full RBAC SystemRole catalogue, scoped to what the
// requesting user is permitted to assign. Used by the SAM Team Members
// invite form's "Advanced…" role picker so admins can select richer
// roles (Site Supervisor, Estimator, Director, …) instead of the
// coarse 4-tier default. The backend (organisation/views/team_members_views/
// assignable_roles_views.py) returns roles already filtered by the
// caller's hierarchy level.
// ---------------------------------------------------------------------------

export interface AssignableRole {
  code: string
  name: string
  description: string
  hierarchy_level: number
  category: string
}

export interface AssignableRolesResponse {
  roles: AssignableRole[]
  user_hierarchy_level: number | null
  is_global_admin: boolean
  can_invite: boolean
}

export async function getAssignableRoles(orgId: number) {
  const { data } = await quotechJson<AssignableRolesResponse>(
    `/api/organisations/${orgId}/assignable-roles/`,
  )
  return data
}

export async function getOrganisationEntities(orgId: number, includeInactive = false) {
  const suffix = includeInactive ? '?include_inactive=true' : ''
  const { data } = await quotechJson<OrganisationEntitiesResponse>(`/api/organisations/${orgId}/entities/${suffix}`)
  return data
}

export async function createOrganisationEntity(orgId: number, payload: Partial<OrganisationEntity>) {
  const { data } = await quotechJson<OrganisationEntity>(`/api/organisations/${orgId}/entities/`, {
    method: 'POST',
    body: payload,
  })
  return data
}

export async function updateOrganisationEntity(orgId: number, entityId: number, payload: Partial<OrganisationEntity>) {
  const { data } = await quotechJson<OrganisationEntity>(`/api/organisations/${orgId}/entities/${entityId}/`, {
    method: 'PATCH',
    body: payload,
  })
  return data
}

export async function deactivateOrganisationEntity(orgId: number, entityId: number) {
  const { data } = await quotechJson<{ message: string }>(`/api/organisations/${orgId}/entities/${entityId}/`, {
    method: 'DELETE',
  })
  return data
}

export async function setDefaultOrganisationEntity(orgId: number, entityId: number) {
  const { data } = await quotechJson<{ message: string; entity: OrganisationEntity }>(`/api/organisations/${orgId}/entities/${entityId}/set-default/`, {
    method: 'POST',
    body: {},
  })
  return data
}

export async function getXeroTenants(orgId: number) {
  const { data } = await quotechJson<XeroTenantListResponse>(`/api/xero/tenants/?tenant=${orgId}`)
  return data
}

export async function linkEntityXeroTenant(orgId: number, entityId: number, tenantId: string | null, tenantName?: string | null) {
  const { data } = await quotechJson<{ message: string; entity: OrganisationEntity }>(`/api/organisations/${orgId}/entities/${entityId}/xero/`, {
    method: 'POST',
    body: {
      tenant_id: tenantId ?? '',
      tenant_name: tenantName ?? '',
    },
  })
  return data
}

export async function getAccessControlSettings(tenantId: number) {
  const { data } = await quotechJson<AccessControlSettings>('/api/settings/access-control/', {
    includeTenant: true,
    tenantId,
  })
  return data
}

export async function saveAccessControlSettings(tenantId: number, payload: Partial<AccessControlSettings>) {
  const { data } = await quotechJson<AccessControlSettings>('/api/settings/access-control/', {
    method: 'PUT',
    includeTenant: true,
    tenantId,
    body: payload,
  })
  return data
}

export async function getModules(tenantId: number) {
  const { data } = await quotechJson<QuotechModuleListResponse>(`/api/modules/?tenant=${tenantId}`)
  return data
}

export async function getRoleCatalog(orgId: number) {
  const { data } = await quotechJson<QuotechRoleListResponse>(`/api/rbac/org/${orgId}/roles/`)
  return data
}

export async function getRoleAssignments(orgId: number) {
  const { data } = await quotechJson<QuotechMemberRoleRecord[]>(`/api/rbac/org/${orgId}/members/`)
  return data
}

export async function assignRole(orgId: number, userId: number, roleCode: string) {
  const { data } = await quotechJson<{ detail: string }>(`/api/rbac/org/${orgId}/members/${userId}/role/`, {
    method: 'PUT',
    body: { role_code: roleCode },
  })
  return data
}

export async function revokeAssignedRole(orgId: number, userId: number) {
  const { data } = await quotechJson<{ detail: string }>(`/api/rbac/org/${orgId}/members/${userId}/role/`, {
    method: 'DELETE',
  })
  return data
}

export async function getCurrentPermissions(orgId?: number | null) {
  const suffix = orgId ? `?org_id=${orgId}` : ''
  const { data } = await quotechJson<Record<string, unknown>>(`/api/rbac/permissions/${suffix}`)
  return data
}

export async function getXeroStatus(tenantId?: number | null) {
  const suffix = tenantId ? `?tenant=${tenantId}` : ''
  const { data } = await quotechJson<IntegrationStatus>(`/api/xero/status/${suffix}`)
  return data
}

export async function getOutlookStatus(tenantId?: number | null) {
  const suffix = tenantId ? `?tenant=${tenantId}` : ''
  const { data } = await quotechJson<IntegrationStatus>(`/api/outlook/status/${suffix}`)
  return data
}

export async function getOneDriveStatus(tenantId?: number | null) {
  const suffix = tenantId ? `?tenant=${tenantId}` : ''
  const { data } = await quotechJson<IntegrationStatus>(`/api/onedrive/status/${suffix}`)
  return data
}

// ─────────────────────────────────────────────────────────────────────
// Per-member activity + sessions (org-admin only)
// ─────────────────────────────────────────────────────────────────────

/**
 * One row from a member's audit log. Mirrors
 * ``organisation/services/audit_logger/models.py::AuditEntry``.
 *
 * ``action`` is a free-form short verb-tag like ``"note_created"`` or
 * ``"file_uploaded"``. The UI renders it via ``humaniseAuditAction()``
 * to present "Created note", "Uploaded file", etc.
 */
export interface MemberAuditEntry {
  id: number
  /** ISO 8601 timestamp. */
  timestamp: string
  /** Verb-style action key. */
  action: string
  /** Resource model name, e.g. ``"note"``, ``"file"``, ``"job"``. */
  resource_type: string
  /** Stringified primary key of the resource. */
  resource_id: string
  /** Human-readable label of the resource (e.g. ``"Job #142"``). */
  resource_name: string
  /** Free-form JSON context — varies by action. */
  details: Record<string, unknown>
  success: boolean
  error_message: string
  user_ip: string | null
  duration_ms: number | null
}

export interface MemberAuditFeedResponse {
  entries: MemberAuditEntry[]
  /** Number of entries actually returned (≤ ``limit``). */
  count: number
  /** ``true`` if more rows exist beyond the page that was returned. */
  has_more: boolean
  window_days: number
}

/**
 * One AuthV2 session (= one device/browser refresh-token row). The
 * shape is symmetrical with what the SELF-Security tab consumes from
 * ``/api/auth/sessions/``, with two extras: ``is_live`` and
 * ``revoked_*`` so admins can also see historical sessions.
 */
export interface MemberSession {
  /** SID (UUID). */
  id: string
  ip: string | null
  user_agent: string
  device: string
  browser: string
  /** ISO 8601. When the refresh token was minted. */
  issued_at: string
  /** ISO 8601 or null. Last time this session refreshed/exchanged. */
  last_used_at: string | null
  refresh_expires_at: string
  /** ISO 8601 or null. */
  revoked_at: string | null
  /** Empty string when ``is_live`` is true. */
  revoked_reason: string
  is_live: boolean
}

export interface MemberSessionsResponse {
  sessions: MemberSession[]
  /** Convenience count of ``is_live = true`` sessions. */
  active_count: number
}

export interface RevokeAllSessionsResult {
  /** Number of sessions that were live before this call. */
  revoked: number
  success: boolean
}

export async function getMemberActivity(
  orgId: number,
  userId: number,
  options: { days?: number; limit?: number } = {},
): Promise<MemberAuditFeedResponse> {
  const params = new URLSearchParams()
  if (options.days != null) params.set('days', String(options.days))
  if (options.limit != null) params.set('limit', String(options.limit))
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const { data } = await quotechJson<MemberAuditFeedResponse>(
    `/api/organisations/${orgId}/members/${userId}/activity/${suffix}`,
  )
  return data
}

export async function getMemberSessions(
  orgId: number,
  userId: number,
  options: { includeRevoked?: boolean } = {},
): Promise<MemberSessionsResponse> {
  const suffix = options.includeRevoked ? '?include_revoked=true' : ''
  const { data } = await quotechJson<MemberSessionsResponse>(
    `/api/organisations/${orgId}/members/${userId}/sessions/${suffix}`,
  )
  return data
}

export async function revokeAllMemberSessions(
  orgId: number,
  userId: number,
): Promise<RevokeAllSessionsResult> {
  const { data } = await quotechJson<RevokeAllSessionsResult>(
    `/api/organisations/${orgId}/members/${userId}/sessions/revoke-all/`,
    { method: 'POST' },
  )
  return data
}
